"use client"

import { useState, useEffect } from "react"
import Link from "next/link"
import { useParams } from "next/navigation"
import { useTranslations } from "next-intl"
import { ArrowRight, List, X } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { LangSwitch } from "@/components/layout/lang-switch"

export default function PublicLayoutClient({ children }: { children: React.ReactNode }) {
  const t = useTranslations("nav")
  const tc = useTranslations("common")
  const params = useParams()
  const locale = (params?.locale as string) || "ru"
  const [menuOpen, setMenuOpen] = useState(false)

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : ""
    return () => { document.body.style.overflow = "" }
  }, [menuOpen])

  const navLinks = [
    { label: t("problems"), href: `/${locale}#problems` },
    { label: t("features"), href: `/${locale}#features` },
    { label: t("pricing"), href: `/${locale}#pricing` },
    { label: t("faq"), href: `/${locale}#faq` },
    { label: t("docs"), href: `/${locale}/docs` },
  ]

  return (
    <div className="min-h-screen bg-canvas-dark">
      <header className="sticky top-0 z-50 flex h-navbar items-center border-b border-hairline-dark bg-canvas-dark px-4 md:px-6">
        <Link href={`/${locale}`} className="text-lg font-bold text-primary shrink-0">
          {tc("brand")}
        </Link>

        <nav className="ml-auto hidden items-center gap-4 md:flex">
          {navLinks.map((l) => (
            <Link key={l.href} href={l.href} className="text-sm text-muted transition-colors hover:text-on-dark">
              {l.label}
            </Link>
          ))}
          <LangSwitch />
          <Link href={`/${locale}/login`}>
            <Button variant="ghost" size="sm">{t("signIn")}</Button>
          </Link>
          <Link href={`/${locale}/register`}>
            <Button variant="primary" size="sm">
              {t("getStarted")}
              <ArrowRight weight="regular" aria-hidden="true" />
            </Button>
          </Link>
        </nav>

        <div className="ml-auto flex items-center gap-2 md:hidden">
          <Link href={`/${locale}/register`}>
            <Button variant="primary" size="sm">
              {t("getStarted")}
            </Button>
          </Link>
          <button
            className="flex h-9 w-9 items-center justify-center rounded-md text-muted transition-colors hover:bg-surface-card-dark hover:text-on-dark"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
          >
            {menuOpen ? <X weight="regular" className="h-5 w-5" /> : <List weight="regular" className="h-5 w-5" />}
          </button>
        </div>
      </header>

      {menuOpen && (
        <div className="fixed inset-0 top-navbar z-40 flex flex-col bg-canvas-dark md:hidden">
          <nav className="flex-1 overflow-y-auto px-4 py-6 space-y-1">
            {navLinks.map((l) => (
              <Link
                key={l.href}
                href={l.href}
                onClick={() => setMenuOpen(false)}
                className="flex h-12 items-center rounded-md px-3 text-base font-medium text-muted transition-colors hover:bg-surface-card-dark hover:text-on-dark"
              >
                {l.label}
              </Link>
            ))}
            <div className="my-2 border-t border-hairline-dark" />
            <Link
              href={`/${locale}/login`}
              onClick={() => setMenuOpen(false)}
              className="flex h-12 items-center rounded-md px-3 text-base font-medium text-muted transition-colors hover:bg-surface-card-dark hover:text-on-dark"
            >
              {t("signIn")}
            </Link>
          </nav>
          <div className="border-t border-hairline-dark px-4 py-4">
            <LangSwitch />
          </div>
        </div>
      )}

      <main>{children}</main>
    </div>
  )
}
